import React, { useState, useMemo, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen, Sparkles, CheckCircle2, XCircle, Loader2, AlertCircle,
  Database, ArrowRight, RefreshCw, ShieldCheck, BookOpenCheck,
  Bot, Activity, User as UserIcon, Clock,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import './RuleCandidates.css';

/**
 * RuleCandidates — operator-gated review of LLM-drafted classification rules.
 *
 * Per CLASSIFICATION_NORTH_STAR.md (Continuous Learning Loop, layer 3):
 * "the LLM drafts, the human ratifies". This page is the ratification
 * surface — every approval becomes a new row in component_mapping_rules
 * so the deterministic STEP-2 layer catches the same misclassification
 * forever after, with zero LLM cost.
 */

function formatCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const seconds = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function StatsStrip({ stats, isLoading, error, activeStatus, onDrillTo }) {
  if (error) {
    return (
      <div className="rc-stats rc-stats--error">
        <AlertCircle size={14} /> Stats failed: {error.message || String(error)}
      </div>
    );
  }
  // Render the tile structure immediately. Each tile shows a spinner
  // in place of the number when data hasn't landed yet — better than
  // a blanket "Loading…" text or a misleading "0".
  const hasStats = !!stats;
  const t = stats?.totals || {};
  const c = stats?.costs || {};
  const approvers = stats?.approvers || [];
  const cronRuns = stats?.cron_runs || [];
  const lastRun = cronRuns[0];

  // Approver split: how many of the approved-status candidates were
  // ratified by the cron (system:auto_approve) vs. by an operator? This
  // is the headline signal that H.11 is self-improving. Today it's 0:6.
  const cronApprovals = approvers.find(a => a.reviewed_by === 'system:auto_approve')?.n || 0;
  const operatorApprovals = approvers
    .filter(a => a.reviewed_by !== 'system:auto_approve')
    .reduce((sum, a) => sum + (a.n || 0), 0);

  // Last cron run state — "ran 12m ago, eval'd 2, declined 2".
  const lastRunRelative = lastRun ? fmtRelative(lastRun.started_at) : '—';
  const lastRunSummary = lastRun
    ? lastRun.fatal_error
      ? 'errored'
      : lastRun.candidates_evaluated === 0
        ? 'ran · no work'
        : `${lastRun.approved}/${lastRun.candidates_evaluated} approved`
    : 'never run';

  const tile = (status, icon, value, label, sub, variant, loading) => {
    const showSpinner = loading && value == null;
    return (
      <button
        type="button"
        className={`rc-stat rc-stat--clickable ${variant ? `rc-stat--${variant}` : ''} ${activeStatus === status ? 'is-active' : ''}`}
        onClick={() => onDrillTo(status)}
        aria-pressed={activeStatus === status}
        title={`Show ${status} candidates`}
      >
        {icon}
        <div>
          <div className="rc-stat__value">
            {showSpinner ? <Loader2 size={18} className="rc-spin" /> : value}
          </div>
          <div className="rc-stat__label">{label}</div>
          {sub && <div className="rc-stat__sub">{sub}</div>}
        </div>
      </button>
    );
  };
  return (
    <div className="rc-stats">
      {tile('pending',  <Sparkles size={14} />,    hasStats ? (t.pending  ?? 0).toLocaleString() : null, 'Pending review',     null, 'primary', !hasStats)}
      {tile('approved', <CheckCircle2 size={14} />, hasStats ? (t.approved ?? 0).toLocaleString() : null, 'Approved — in rules', null, 'success', !hasStats)}
      {tile('rejected', <XCircle size={14} />,     hasStats ? (t.rejected ?? 0).toLocaleString() : null, 'Rejected',           null, null,     !hasStats)}

      {/* Approver split — cron vs operator. The whole point of H.11
          auto-approve is that cron should be doing the easy ones. If
          this tile says 0/N, the cron's never promoted anything and
          the loop isn't actually self-improving yet. */}
      <div className="rc-stat" title={approvers.map(a => `${a.reviewed_by}: ${a.n}`).join('\n')}>
        <Bot size={14} />
        <div>
          <div className="rc-stat__value">
            {!hasStats
              ? <Loader2 size={18} className="rc-spin" />
              : `${cronApprovals.toLocaleString()} / ${(cronApprovals + operatorApprovals).toLocaleString()}`}
          </div>
          <div className="rc-stat__label">Auto-approved by cron</div>
          <div className="rc-stat__sub">
            {hasStats
              ? `${operatorApprovals.toLocaleString()} by operator (${cronApprovals + operatorApprovals > 0 ? Math.round(100 * cronApprovals / (cronApprovals + operatorApprovals)) : 0}% automated)`
              : '—'}
          </div>
        </div>
      </div>

      {/* Cron health — last run timing + result. Distinguishes
          "ran 10min ago, found nothing eligible" from "hasn't run in
          days". rule_autoapprove_runs is the source. */}
      <div className="rc-stat"
        title={lastRun
          ? `Run #${lastRun.run_id} · started ${new Date(lastRun.started_at).toLocaleString()}`
            + (lastRun.dry_run ? ' (dry run)' : '')
            + `\nevaluated=${lastRun.candidates_evaluated} approved=${lastRun.approved} declined=${lastRun.declined} errored=${lastRun.errored}`
          : 'No cron runs logged yet'}>
        <Activity size={14} />
        <div>
          <div className="rc-stat__value">
            {!hasStats ? <Loader2 size={18} className="rc-spin" /> : lastRunRelative}
          </div>
          <div className="rc-stat__label">Cron last run</div>
          <div className="rc-stat__sub">{hasStats ? lastRunSummary : '—'}</div>
        </div>
      </div>

      <div className="rc-stat">
        <Database size={14} />
        <div>
          <div className="rc-stat__value">
            {!hasStats
              ? <Loader2 size={18} className="rc-spin" />
              : `$${Number(c.cost_total_usd || 0).toFixed(2)}`}
          </div>
          <div className="rc-stat__label">Total drafting cost</div>
          <div className="rc-stat__sub">
            {hasStats ? `$${Number(c.cost_24h_usd || 0).toFixed(2)} last 24h · $${Number(c.cost_30d_usd || 0).toFixed(2)} last 30d` : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

function CandidateCard({ candidate, onApprove, onReject, busy }) {
  const [editing, setEditing] = useState(false);
  const [overrides, setOverrides] = useState({
    rule_name: candidate.draft_rule_name,
    match_pattern: candidate.draft_match_pattern,
    priority: candidate.draft_priority,
    confidence_weight: Number(candidate.draft_confidence),
  });
  const [notes, setNotes] = useState('');

  const submitApprove = () => {
    onApprove(candidate.candidate_id, {
      overrides: editing ? overrides : {},
      review_notes: notes,
    });
  };
  const submitReject = () => {
    onReject(candidate.candidate_id, notes || 'Operator rejected.');
  };

  return (
    <article className="rc-card">
      <header className="rc-card__head">
        <div className="rc-card__title">
          <Sparkles size={14} className="rc-card__title-icon" />
          <span className="rc-card__rulename">{candidate.draft_rule_name}</span>
          <span className="rc-tag rc-tag--mono">{candidate.draft_match_type}</span>
          <span className="rc-tag">prio {candidate.draft_priority}</span>
          <span className="rc-tag">conf {(Number(candidate.draft_confidence) * 100).toFixed(0)}%</span>
        </div>
        <div className="rc-card__meta">
          <span title="Model + cost">
            {candidate.drafted_by_model} · ${Number(candidate.llm_cost_usd || 0).toFixed(4)}
          </span>
          <span title="Tokens">
            {candidate.llm_input_tokens}↓ / {candidate.llm_output_tokens}↑ tok
          </span>
          <span title="When drafted">{fmtRelative(candidate.drafted_at)}</span>
        </div>
      </header>

      {/* H.11.3 observability — for pending candidates the backend
          computes whether the cron WOULD auto-approve this row, and
          if not, the exact failing criterion. Saves the operator from
          guessing why a 0.85-confidence draft hasn't been picked up
          (answer: confidence floor is 0.90).
          For terminal-status candidates this banner is suppressed. */}
      {candidate.status === 'pending' && candidate.auto_approve_reason && (
        <div className={`rc-autoapprove rc-autoapprove--${candidate.auto_approve_eligible ? 'ready' : 'blocked'}`}>
          {candidate.auto_approve_eligible
            ? <><Bot size={12} /> Cron will auto-approve this on its next run · <em>{candidate.auto_approve_reason}</em></>
            : <><Bot size={12} /> Cron skips: <strong>{candidate.auto_approve_reason}</strong></>
          }
        </div>
      )}

      <section className="rc-card__correction">
        <div className="rc-card__pane">
          <span className="rc-meta-label">Was</span>
          <strong>{candidate.old_component_name || <em className="rc-muted">unclassified</em>}</strong>
          <span className="rc-mono rc-muted">{candidate.old_vendor_name || '—'}</span>
        </div>
        <ArrowRight size={14} className="rc-card__arrow" />
        <div className="rc-card__pane">
          <span className="rc-meta-label">Corrected to</span>
          <strong>{candidate.new_component_name}</strong>
          <span className="rc-mono rc-muted">
            {candidate.new_vendor_name || '—'} · {candidate.new_category || '—'}
          </span>
        </div>
      </section>

      <section className="rc-card__source">
        <span className="rc-meta-label">Source item</span>
        <p className="rc-card__source-desc" title={candidate.source_description}>
          {candidate.source_description?.slice(0, 220) || <em className="rc-muted">no description</em>}
          {candidate.source_description?.length > 220 ? '…' : ''}
        </p>
        <div className="rc-card__source-meta">
          {candidate.source_supplier && <span><strong>Supplier:</strong> {candidate.source_supplier}</span>}
          {candidate.source_amount != null && <span><strong>Amount:</strong> {formatCurrency(candidate.source_amount)}</span>}
          {candidate.source_wave_id && (
            <span className="rc-mono rc-muted" title="Audit wave id">{candidate.source_wave_id}</span>
          )}
        </div>
      </section>

      <section className="rc-card__rule">
        <div className="rc-card__rule-head">
          <BookOpenCheck size={14} /> Proposed rule
          <button
            className="rc-edit-btn"
            onClick={() => setEditing(e => !e)}
            type="button"
          >
            {editing ? 'Cancel edit' : 'Edit before approving'}
          </button>
        </div>
        {editing ? (
          <div className="rc-card__rule-edit">
            <label>
              <span>Rule name</span>
              <input
                value={overrides.rule_name}
                onChange={e => setOverrides(o => ({ ...o, rule_name: e.target.value }))}
              />
            </label>
            <label>
              <span>Match pattern</span>
              <input
                className="rc-mono"
                value={overrides.match_pattern}
                onChange={e => setOverrides(o => ({ ...o, match_pattern: e.target.value }))}
              />
            </label>
            <div className="rc-card__rule-edit-row">
              <label>
                <span>Priority</span>
                <input
                  type="number"
                  value={overrides.priority}
                  onChange={e => setOverrides(o => ({ ...o, priority: parseInt(e.target.value, 10) || 0 }))}
                />
              </label>
              <label>
                <span>Confidence (0–1)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={overrides.confidence_weight}
                  onChange={e => setOverrides(o => ({ ...o, confidence_weight: Number(e.target.value) }))}
                />
              </label>
            </div>
          </div>
        ) : (
          <div className="rc-card__rule-display">
            <div className="rc-mono rc-card__pattern">
              <span className="rc-tag rc-tag--mono">{candidate.draft_match_type}</span>
              {candidate.draft_match_pattern}
            </div>
            <div className="rc-card__type">
              <strong>type:</strong> {candidate.draft_component_type}
            </div>
          </div>
        )}
        {candidate.llm_reasoning && (
          <p className="rc-card__reasoning">
            <span className="rc-meta-label">Reasoning</span>
            {candidate.llm_reasoning}
          </p>
        )}
      </section>

      <section className="rc-card__notes">
        <label>
          <span className="rc-meta-label">Review notes (optional)</span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Why are you approving/rejecting? Helpful for the audit trail."
            rows={2}
          />
        </label>
      </section>

      <footer className="rc-card__actions">
        <button
          className="rc-action rc-action--approve"
          onClick={submitApprove}
          disabled={busy}
        >
          {busy ? <Loader2 size={12} className="rc-spin" /> : <CheckCircle2 size={12} />}
          {editing ? 'Approve with overrides' : 'Approve as-is'}
        </button>
        <button
          className="rc-action rc-action--reject"
          onClick={submitReject}
          disabled={busy}
        >
          <XCircle size={12} /> Reject
        </button>
      </footer>
    </article>
  );
}

function RuleCandidates() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  // H.11.2: when the backend's regression gate returns 409
  // 'regression_gate_blocked', we surface an inline panel (instead of
  // alert) with the conflict analysis + a "Force approve" button.
  const [regressionBlock, setRegressionBlock] = useState(null);  // { id, body }
  // Non-gate approval failures (duplicate rule_name, etc.) render as an
  // inline notice instead of alert(); cleared on dismiss or next success.
  const [approveError, setApproveError] = useState(null);  // { id, message }

  const listQuery = useQuery({
    queryKey: ['rule-candidates', 'list', statusFilter],
    queryFn: () => apiClient.get('/api/classification/rule-candidates', {
      // For the pending view, ask the backend to compute auto_approve_reason
      // per row by running the 9-criterion gate (incl. regression). The
      // gate query is cheap when the candidate count is small (single-digit);
      // we only opt in for pending because that's the only status where the
      // reason is actionable.
      params: { status: statusFilter, limit: 100, with_reasons: statusFilter === 'pending' ? 'true' : undefined },
    }),
    staleTime: 30_000,
  });

  const statsQuery = useQuery({
    queryKey: ['rule-candidates', 'stats'],
    queryFn: () => apiClient.get('/api/classification/rule-candidates/stats'),
    staleTime: 60_000,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, body }) =>
      apiClient.post(`/api/classification/rule-candidates/${id}/approve`, body),
    onSuccess: (data, { id }) => {
      logger.ui.info('RuleCandidates', 'approved', {
        candidate_id: id, merged_rule_id: data?.merged_rule_id,
      });
      setApproveError(null);
      queryClient.invalidateQueries({ queryKey: ['rule-candidates'] });
    },
    onError: (err, { id, body }) => {
      logger.ui.error('RuleCandidates', 'approve_failed', {
        candidate_id: id, message: err?.message,
      });
      // H.11.2: when the regression gate blocks, render the conflict
      // analysis inline (not an alert) so the operator can see WHICH
      // rows would be re-flipped and decide whether to force.
      if (err?.status === 409 && err?.data?.error === 'regression_gate_blocked') {
        setRegressionBlock({ id, body: body || {}, regression: err.data.regression, message: err.data.message });
        return;
      }
      // Other failures (duplicate rule_name, etc.) get the inline notice.
      setApproveError({ id, message: err?.message });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reviewNotes }) =>
      apiClient.post(`/api/classification/rule-candidates/${id}/reject`, { review_notes: reviewNotes }),
    onSuccess: (_data, { id }) => {
      logger.ui.info('RuleCandidates', 'rejected', { candidate_id: id });
      queryClient.invalidateQueries({ queryKey: ['rule-candidates'] });
    },
  });

  const handleApprove = useCallback((id, body) => approveMutation.mutate({ id, body }), [approveMutation]);
  const handleReject = useCallback((id, reviewNotes) => rejectMutation.mutate({ id, reviewNotes }), [rejectMutation]);

  const candidates = listQuery.data?.candidates || [];

  return (
    <div className="rc-page">
      <header className="rc-page__head">
        <div className="rc-page__title">
          <BookOpen size={22} />
          <div>
            <h1>Rule candidates</h1>
            <p className="rc-page__sub">
              Sonnet-drafted classification rules awaiting operator approval.
              Each approval becomes a row in <code>component_mapping_rules</code> —
              future similar items classify deterministically (free, instant)
              instead of paying for an LLM call. The LLM drafts, you ratify
              (CLASSIFICATION_NORTH_STAR.md, Continuous Learning Loop, layer 3).
            </p>
          </div>
        </div>
        <div className="rc-page__principles">
          <span className="rc-pill"><ShieldCheck size={11} /> LLM drafts · human ratifies</span>
        </div>
      </header>

      <StatsStrip
        stats={statsQuery.data}
        isLoading={statsQuery.isLoading}
        error={statsQuery.error}
        activeStatus={statusFilter}
        onDrillTo={(status) => setStatusFilter(status)}
      />

      {approveError && (
        <div className="rc-approve-error" role="alert">
          <AlertCircle size={14} />
          <span>Approval of candidate #{approveError.id} failed: {approveError.message}</span>
          <button
            className="rc-approve-error__dismiss"
            onClick={() => setApproveError(null)}
            title="Dismiss"
          >
            <XCircle size={14} />
          </button>
        </div>
      )}

      {regressionBlock && (
        <div style={{
          margin: '14px 0',
          padding: '14px 16px',
          border: '2px solid #dc2626',
          background: 'rgba(220, 38, 38, 0.06)',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertCircle size={18} color="#dc2626" />
            <strong style={{ fontSize: 14, color: '#dc2626' }}>
              Regression gate BLOCKED candidate #{regressionBlock.id}
            </strong>
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text, #1f2937)', marginBottom: 10 }}>
            {regressionBlock.message}
          </div>
          {regressionBlock.regression && (
            <>
              <div style={{ display: 'flex', gap: 18, fontSize: 12, color: 'var(--color-text-muted, #6b7280)', marginBottom: 10 }}>
                <span><strong>Matched:</strong> {regressionBlock.regression.matched}</span>
                <span><strong>Correct:</strong> {regressionBlock.regression.matched_correct}</span>
                <span style={{ color: '#dc2626' }}><strong>Conflict:</strong> {regressionBlock.regression.matched_conflict}</span>
                <span><strong>Unclassified:</strong> {regressionBlock.regression.matched_unclassified}</span>
                <span><strong>Conflict ratio:</strong> {(regressionBlock.regression.conflict_ratio * 100).toFixed(1)}%</span>
              </div>
              {regressionBlock.regression.samples?.length > 0 && (
                <details style={{ marginBottom: 10 }}>
                  <summary style={{ fontSize: 12, cursor: 'pointer', color: 'var(--color-text-muted, #6b7280)' }}>
                    Sample conflicts (first 5)
                  </summary>
                  <ul style={{ fontSize: 11, marginTop: 6, paddingLeft: 20 }}>
                    {regressionBlock.regression.samples.map((s, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        item={s.item_id} currently="{s.current_component_name}"
                        <code style={{ marginLeft: 6, fontSize: 10 }}>{s.description}</code>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={async () => {
                if (!(await confirmDialog(
                  `Force-approve candidate #${regressionBlock.id}?\n\n` +
                  `This will re-classify ${regressionBlock.regression?.matched_conflict || 0} currently-correct rows to a different component. ` +
                  `Your decision will be logged in review_notes for audit.`
                ))) return;
                approveMutation.mutate({
                  id: regressionBlock.id,
                  body: { ...(regressionBlock.body || {}), force: true },
                });
                setRegressionBlock(null);
              }}
              style={{
                padding: '8px 12px',
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Force approve anyway
            </button>
            <button
              type="button"
              onClick={() => setRegressionBlock(null)}
              style={{
                padding: '8px 12px',
                background: 'transparent',
                color: 'var(--color-text, #1f2937)',
                border: '1px solid var(--color-border, #d1d5db)',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel — leave pending
            </button>
          </div>
        </div>
      )}

      <div className="rc-toolbar">
        <div className="rc-filter">
          <label className="rc-filter__label">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="pending">Pending (default)</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
        </div>
        <button
          className="rc-filter__refresh"
          onClick={() => { listQuery.refetch(); statsQuery.refetch(); }}
          disabled={listQuery.isFetching}
          title="Refresh"
        >
          <RefreshCw size={14} className={listQuery.isFetching ? 'rc-spin' : ''} />
        </button>
      </div>

      <main className="rc-list">
        {listQuery.isLoading && (
          <div className="rc-state"><Loader2 size={18} className="rc-spin" /> Loading…</div>
        )}
        {listQuery.error && (
          <div className="rc-state rc-state--error">
            <AlertCircle size={18} /> Failed to load: {listQuery.error.message}
          </div>
        )}
        {!listQuery.isLoading && !listQuery.error && candidates.length === 0 && (
          <div className="rc-state rc-state--empty">
            <ShieldCheck size={28} />
            <h3>No {statusFilter === 'all' ? '' : statusFilter} candidates</h3>
            <p>
              {statusFilter === 'pending'
                ? "Nothing waiting on review. Apply a flag-driven reclassification to generate one."
                : `No candidates match the "${statusFilter}" filter.`}
            </p>
          </div>
        )}
        {candidates.map(c => (
          <CandidateCard
            key={c.candidate_id}
            candidate={c}
            onApprove={handleApprove}
            onReject={handleReject}
            busy={
              (approveMutation.isPending && approveMutation.variables?.id === c.candidate_id) ||
              (rejectMutation.isPending && rejectMutation.variables?.id === c.candidate_id)
            }
          />
        ))}
      </main>

      <footer className="rc-page__foot">
        Showing {candidates.length} of {listQuery.data?.total || 0} {statusFilter !== 'all' ? statusFilter : ''} candidates.
      </footer>
    </div>
  );
}

export default RuleCandidates;
